export interface QuizQuestion {
  id: number;
  category: string;
  title: string;
  options: QuizOption[];
}

export interface QuizOption {
  label: string;
  icon: string;
  scores: Record<string, number>;
}

export interface QuizResult {
  id: string;
  flag: string;
  matchScore: number;
  title: string;
  intro: string;
  reasons: { icon: string; label: string }[];
  stats: { icon: string; value: string; label: string }[];
  awareOf: string[];
  ctaBullets: string[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    category: "Treatment Type",
    title: "What type of dental treatment are you mainly considering?",
    options: [
      { label: "Dental implants (single or multiple)", icon: "🦷", scores: { hungary: 3, poland: 2, turkey: 1, spain: 1 } },
      { label: "Full-arch / All-on-4 / All-on-6", icon: "🦷", scores: { hungary: 3, turkey: 3, poland: 1, spain: 1 } },
      { label: "Crowns or bridges", icon: "🦷", scores: { poland: 3, spain: 2, hungary: 1, turkey: 1 } },
      { label: "Veneers / cosmetic dentistry", icon: "✨", scores: { turkey: 3, spain: 2, poland: 1, hungary: 0 } },
      { label: "I'm not sure yet", icon: "🤔", scores: { poland: 2, spain: 2, hungary: 1, turkey: 1 } },
    ],
  },
  {
    id: 2,
    category: "Budget Sensitivity",
    title: "Which statement best describes your priority?",
    options: [
      { label: "Lowest possible price", icon: "💰", scores: { turkey: 3, hungary: 1, poland: 1, spain: 0 } },
      { label: "Balance of cost and long-term outcome", icon: "⚖️", scores: { poland: 3, spain: 2, hungary: 2, turkey: 1 } },
      { label: "Long-term outcome matters more than price", icon: "🏆", scores: { hungary: 3, spain: 3, poland: 1, turkey: 0 } },
    ],
  },
  {
    id: 3,
    category: "Risk Tolerance",
    title: "How would you describe your risk tolerance?",
    options: [
      { label: "Low — I want the safest, most conservative option", icon: "🛡️", scores: { hungary: 3, spain: 3, poland: 2, turkey: 0 } },
      { label: "Medium — I'm comfortable if risks are explained clearly", icon: "📋", scores: { poland: 3, hungary: 2, spain: 2, turkey: 1 } },
      { label: "High — I'm prioritising speed and cost", icon: "⚡", scores: { turkey: 3, hungary: 1, poland: 1, spain: 0 } },
    ],
  },
  {
    id: 4,
    category: "Travel Flexibility",
    title: "How flexible are you with travel?",
    options: [
      { label: "One short trip only", icon: "✈️", scores: { turkey: 3, spain: 2, poland: 1, hungary: 0 } },
      { label: "Two trips are fine if needed", icon: "🔄", scores: { hungary: 3, poland: 2, spain: 1, turkey: 0 } },
      { label: "I'm flexible if the outcome is better", icon: "🌍", scores: { hungary: 3, spain: 2, poland: 2, turkey: 1 } },
      { label: "I want somewhere I can easily return to for aftercare", icon: "🏠", scores: { spain: 3, poland: 2, hungary: 1, turkey: 0 } },
    ],
  },
  {
    id: 5,
    category: "Communication & Aftercare",
    title: "Which is most important to you?",
    options: [
      { label: "Clear communication and conservative planning", icon: "💬", scores: { hungary: 3, poland: 2, spain: 1, turkey: 0 } },
      { label: "Speed and convenience", icon: "⚡", scores: { turkey: 3, spain: 1, poland: 1, hungary: 0 } },
      { label: "EU-based care and regulations", icon: "🇪🇺", scores: { spain: 3, poland: 3, hungary: 2, turkey: 0 } },
    ],
  },
  {
    id: 6,
    category: "Decision Timeline",
    title: "When are you hoping to move forward?",
    options: [
      { label: "As soon as possible", icon: "🚀", scores: { turkey: 3, spain: 1, poland: 1, hungary: 1 } },
      { label: "In the next 3–6 months", icon: "📅", scores: { hungary: 3, poland: 2, spain: 2, turkey: 1 } },
      { label: "I'm still researching", icon: "🔍", scores: { spain: 3, poland: 2, hungary: 2, turkey: 1 } },
    ],
  },
];

export const quizResults: Record<string, QuizResult> = {
  hungary: {
    id: "hungary",
    flag: "🇭🇺",
    matchScore: 88,
    title: "Hungary may be the best fit for your dental treatment",
    intro:
      "Based on your answers, Hungary often suits people who prioritise long-term outcomes, conservative treatment planning, and implant-focused care. Hungary has one of the longest-established dental tourism infrastructures in Europe, particularly for implants and complex restorative work.",
    reasons: [
      { icon: "🦷", label: "Implant specialist" },
      { icon: "🇪🇺", label: "EU-regulated" },
      { icon: "🗓️", label: "Phased planning" },
      { icon: "🏆", label: "Established track record" },
    ],
    stats: [
      { icon: "💰", value: "50–70%", label: "potential savings" },
      { icon: "✈️", value: "2", label: "trips typical" },
      { icon: "⭐", value: "30+", label: "yrs dental tourism" },
    ],
    awareOf: [
      "Treatment often requires two trips",
      "Headline prices may exclude crowns or lab work",
      "Planning is typically more cautious and phased",
    ],
    ctaBullets: [
      "Typical implant pricing by country",
      "What's usually included vs excluded",
      "The exact questions to ask before paying any deposit",
    ],
  },
  turkey: {
    id: "turkey",
    flag: "🇹🇷",
    matchScore: 82,
    title: "Turkey could suit your goals — with careful checks",
    intro:
      "Based on your answers, Turkey may suit those prioritising speed, cosmetic outcomes, and competitive pricing. However, quality and aftercare arrangements can vary significantly between clinics.",
    reasons: [
      { icon: "💎", label: "Cosmetic focus" },
      { icon: "⚡", label: "Fast turnaround" },
      { icon: "💵", label: "Competitive price" },
      { icon: "🔍", label: "Verify carefully" },
    ],
    stats: [
      { icon: "💰", value: "60–75%", label: "potential savings" },
      { icon: "✈️", value: "1", label: "trip typical" },
      { icon: "⚠️", value: "High", label: "due-diligence needed" },
    ],
    awareOf: [
      "Strong marketing does not equal consistent standards",
      "Material quality and lab work must be verified",
      "Pressure to move quickly is common — ask questions first",
    ],
    ctaBullets: [
      "What Turkey prices usually include (and don't)",
      "Red flags to watch for",
      "How to protect yourself before committing",
    ],
  },
  poland: {
    id: "poland",
    flag: "🇵🇱",
    matchScore: 85,
    title: "Poland may offer the balance you're looking for",
    intro:
      "Based on your answers, Poland often suits people who want EU-based care, transparent pricing, and a balance between cost and caution. It's frequently chosen by patients who want fewer unknowns and easier travel logistics.",
    reasons: [
      { icon: "🇪🇺", label: "EU-regulated" },
      { icon: "🤝", label: "Transparent pricing" },
      { icon: "🛡️", label: "Fewer pressure tactics" },
      { icon: "✈️", label: "Easy EU travel" },
    ],
    stats: [
      { icon: "💰", value: "40–60%", label: "potential savings" },
      { icon: "✈️", value: "1–2", label: "trips typical" },
      { icon: "📋", value: "Clear", label: "treatment plans" },
    ],
    awareOf: [
      "Smaller dental tourism ecosystem",
      "Less bundled \"packages\"",
      "You may need to organise travel yourself",
    ],
    ctaBullets: [
      "How Poland compares to Hungary, Turkey and Spain",
      "What to check before booking",
      "How to avoid hidden costs",
    ],
  },
  spain: {
    id: "spain",
    flag: "🇪🇸",
    matchScore: 86,
    title: "Spain looks like a strong fit — premium EU care with easy travel",
    intro:
      "Based on your answers, Spain suits you well. You've indicated that aftercare access and EU-regulated quality matter — and Spain delivers on both. With flights of just 2–3 hours from most UK airports, returning for follow-ups or addressing any issues is far more practical than with longer-haul destinations.",
    reasons: [
      { icon: "🇪🇺", label: "EU-regulated" },
      { icon: "🏠", label: "Easy aftercare" },
      { icon: "✈️", label: "Short flight" },
      { icon: "🏆", label: "Premium quality" },
    ],
    stats: [
      { icon: "💰", value: "30–50%", label: "potential savings" },
      { icon: "✈️", value: "2–3 hrs", label: "from UK airports" },
      { icon: "🛡️", value: "Low", label: "risk level" },
    ],
    awareOf: [
      "Prices are higher than Hungary or Poland — but still well below UK private rates",
      "Fewer \"dental tourism package\" deals — you may need to research clinics independently",
      "English-speaking clinics are common in major cities and coastal areas, but confirm beforehand",
    ],
    ctaBullets: [
      "What EU regulations mean for your protection",
      "How to find and vet Spanish clinics experienced with UK patients",
      "The exact questions to ask before paying any deposit",
    ],
  },
  balanced: {
    id: "balanced",
    flag: "🌍",
    matchScore: 79,
    title: "Your profile points to a balanced approach — with Poland as a strong starting point",
    intro:
      "Based on your answers, your priorities span multiple factors — which is completely normal when researching dental treatment abroad for the first time. Poland often suits people in this position: EU-based, transparent, and with fewer high-pressure sales tactics than some other destinations.",
    reasons: [
      { icon: "⚖️", label: "Multi-factor profile" },
      { icon: "🔍", label: "Compare countries" },
      { icon: "📋", label: "Request itemised quotes" },
      { icon: "🛡️", label: "Prioritise safety" },
    ],
    stats: [
      { icon: "🌍", value: "4", label: "countries to compare" },
      { icon: "📋", value: "Always", label: "ask for itemised quote" },
      { icon: "🤔", value: "Research", label: "before committing" },
    ],
    awareOf: [
      "Compare at least two countries before committing",
      "Always request itemised quotes — not just headline prices",
      "Ask about aftercare and who to contact if something goes wrong",
    ],
    ctaBullets: [
      "Side-by-side country comparison",
      "Exact questions to ask every clinic",
      "How to protect yourself regardless of where you go",
    ],
  },
};

export function calculateResult(answers: number[]): string {
  const scores: Record<string, number> = { hungary: 0, turkey: 0, poland: 0, spain: 0 };

  answers.forEach((answerIdx, qIdx) => {
    const question = quizQuestions[qIdx];
    if (!question) return;
    const option = question.options[answerIdx];
    if (!option) return;
    Object.entries(option.scores).forEach(([country, score]) => {
      scores[country] = (scores[country] || 0) + score;
    });
  });

  // Check for balanced (all scores within 2 points of each other)
  const values = Object.values(scores);
  const max = Math.max(...values);
  const min = Math.min(...values);
  if (max - min <= 2 && max < 10) {
    return "balanced";
  }

  let winner = "hungary";
  let highest = -1;
  Object.entries(scores).forEach(([country, score]) => {
    if (score > highest) {
      highest = score;
      winner = country;
    }
  });

  return winner;
}